import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";

/*
  Microsoft SSO is the only way in — see AuthContext.jsx. This page is both
  the starting point (the button) and the landing point: Microsoft redirects
  back here, supabase-js reads the session out of the URL, and PublicOnly in
  App.js moves a signed-in user on to wherever they were headed.
*/

// The four-square Microsoft logo, as Microsoft's sign-in button guidelines ask.
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Supabase reports a failed or cancelled Microsoft sign-in as
// ?error_description=... (or in the #hash, for the implicit flow).
function readOAuthError() {
  const fromSearch = new URLSearchParams(window.location.search).get("error_description");
  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("error_description");
  return fromSearch || fromHash || "";
}

export default function LoginPage() {
  const { loginWithMicrosoft, loginWithPassword, authError } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [error, setError] = useState(() => readOAuthError());

  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  // Where they were trying to go (a report deep-linked from an email), as
  // remembered by ProtectedRoute. AuthContext validates it before using it.
  const from = location.state?.from;
  const nextPath = from?.pathname ? `${from.pathname}${from.search || ""}` : null;

  const onMicrosoft = async () => {
    setLoading(true);
    setError("");
    try {
      await loginWithMicrosoft(nextPath);
      // On success the browser is already navigating away to Microsoft.
    } catch (err) {
      const msg = formatApiErrorDetail(err);
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  const onAdminSubmit = async (e) => {
    e.preventDefault();
    setAdminBusy(true);
    setError("");
    try {
      await loginWithPassword(adminEmail, adminPassword);
      // PublicOnly moves the signed-in user on.
    } catch (err) {
      setError(err?.message === "Invalid login credentials" ? "Invalid email or password." : formatApiErrorDetail(err));
      setAdminBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 overflow-auto grid lg:grid-cols-2 grid-rows-[minmax(100%,auto)] bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
           style={{ background: "linear-gradient(135deg, #003B95 0%, #005FB2 55%, #0176D3 100%)" }}>
        <div aria-hidden className="absolute inset-0 opacity-[0.08]"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "40px 40px" }}/>
        <div className="relative z-10 flex items-center gap-2">
          <div className="bg-white rounded-md px-3 py-2">
            <img src="/indorama-logo.png" alt="Indorama" className="h-8 w-auto object-contain" />
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="trk-uppercase-label text-white/70">Initiative Board</div>
          <h1 className="font-display text-5xl font-extrabold tracking-tight leading-tight mt-4">
            Every initiative<br />tracked. Every<br />signal decoded.
          </h1>
          <p className="text-white/80 mt-6 leading-relaxed">
            A high-density operating console for teams that ship. Initiatives, projects,
            tasks and docs — orchestrated with AI-generated status reports.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-6 text-sm">
          <div className="border-l border-white/25 pl-3">
            <div className="font-mono text-2xl font-medium">04</div>
            <div className="trk-uppercase-label text-white/70">Statuses</div>
          </div>
          <div className="border-l border-white/25 pl-3">
            <div className="font-mono text-2xl font-medium">∞</div>
            <div className="trk-uppercase-label text-white/70">Projects</div>
          </div>
          <div className="border-l border-white/25 pl-3">
            <div className="font-mono text-2xl font-medium">AI</div>
            <div className="trk-uppercase-label text-white/70">Reports</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8 lg:hidden">
            <img src="/indorama-logo.png" alt="Indorama" className="h-8 w-auto object-contain" />
          </div>
          <div className="mb-8">
            <div className="trk-uppercase-label mb-2">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">Continue to your workspace</h2>
            <p className="text-sm text-[#475569] mt-2">
              Use your <span className="font-mono text-[#0176D3]">@indorama.com</span> Microsoft account.
            </p>
          </div>
          <div className="space-y-4">
            {error && (
              <div className="text-sm rounded-md p-3" data-testid="login-error"
                   style={{ color: "#B91C1C", background: "#FEE2E2", border: "1px solid #FCA5A5" }}>{error}</div>
            )}
            <Button type="button" onClick={onMicrosoft} disabled={loading} data-testid="login-microsoft-button"
                    className="w-full h-11 rounded-md bg-white hover:bg-zinc-50 text-[#0F172A] border border-[#8C8C8C] font-medium transition-colors flex items-center justify-center gap-3">
              <MicrosoftLogo />
              {loading ? "Redirecting to Microsoft…" : "Sign in with Microsoft"}
            </Button>
            <p className="text-xs text-[#94A3B8] text-center">
              First time here? Signing in creates your account automatically.
            </p>
            <div className="pt-6 text-center">
              {!showAdmin ? (
                <button type="button" onClick={() => setShowAdmin(true)} data-testid="admin-signin-toggle"
                        className="text-xs text-[#94A3B8] hover:text-[#0176D3] hover:underline">
                  Sign In
                </button>
              ) : (
                <form onSubmit={onAdminSubmit} className="space-y-2 text-left border-t border-[#E2E8F0] pt-4" data-testid="admin-signin-form">
                  <div className="trk-uppercase-label">Admin sign in</div>
                  <input type="email" required autoComplete="username" placeholder="Email" value={adminEmail}
                         onChange={(e) => setAdminEmail(e.target.value)} data-testid="admin-email"
                         className="w-full h-9 px-3 text-sm rounded-md border border-[#CBD5E1] focus:outline-none focus:border-[#0176D3]" />
                  <input type="password" required autoComplete="current-password" placeholder="Password" value={adminPassword}
                         onChange={(e) => setAdminPassword(e.target.value)} data-testid="admin-password"
                         className="w-full h-9 px-3 text-sm rounded-md border border-[#CBD5E1] focus:outline-none focus:border-[#0176D3]" />
                  <Button type="submit" disabled={adminBusy} data-testid="admin-signin-submit"
                          className="w-full h-9 rounded-md bg-[#0176D3] hover:bg-[#005FB2] text-white text-sm">
                    {adminBusy ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
